import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal, ClipReveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { CTABand } from '@/components/site/CTABand';
import { WRAP, SECTION, SectionHead } from '@/components/site/ServicePageParts';
import { SITE, SERVICES } from '@/data/site';
import { HERO_IMAGES } from '@/data/heroImages';
import { motionOn } from '@/lib/motion';

gsap.registerPlugin(ScrollTrigger);

const EASE = '[transition-timing-function:var(--ease-out)]';
const LENGTHS = ['6–10 wks', '8–14 wks', '10–16 wks', '8–14 wks', '10–16 wks', '6–12 wks'];

const ENGAGEMENTS = [
  {
    title: 'Sprint',
    price: '$9.9k–19k',
    length: '2–4 weeks',
    blurb: 'A focused discovery + prototype sprint. Best for new ideas with fuzzy edges.',
  },
  {
    title: 'Build',
    price: '$39k–110k',
    length: '8–16 weeks',
    blurb: 'A complete design + engineering engagement to ship v1 of a product or site.',
  },
  {
    title: 'Partner',
    price: '$2.4k+ / mo',
    length: '6 months+',
    blurb: 'Fractional product team for founders who need an in-house gear box without the hires.',
  },
];

const Disc = () => (
  <span aria-hidden className="h-12 w-12 md:h-14 md:w-14 shrink-0 rounded-full border border-white/15 grid place-items-center transition-colors duration-300 group-hover/link:bg-white group-hover/link:text-ink-bg group-hover/link:border-white">
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" className={`transition-transform [transition-duration:550ms] ${EASE} group-hover/link:-rotate-45`}><path d="M2 8h12M9 3l5 5-5 5" /></svg>
  </span>
);

export default function Services() {
  const indexRef = useRef(null);

  // Gradient rule under each row number draws in; row image drifts (parallax).
  useEffect(() => {
    if (!motionOn() || !indexRef.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.svc-row').forEach((row) => {
        gsap.fromTo(
          row.querySelector('.svc-rule'),
          { scaleX: 0 },
          {
            scaleX: 1,
            ease: 'power2.out',
            scrollTrigger: { trigger: row, start: 'top 80%', end: 'top 40%', scrub: 0.5 },
          }
        );
        const img = row.querySelector('.svc-img');
        if (img) {
          gsap.fromTo(img, { yPercent: -5 }, {
            yPercent: 5,
            ease: 'none',
            scrollTrigger: { trigger: row, start: 'top bottom', end: 'bottom top', scrub: true },
          });
        }
      });
    }, indexRef);
    return () => ctx.revert();
  }, []);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-[12vw] -top-[18vw] h-[70vw] w-[70vw] max-h-[1000px] max-w-[1000px] rounded-full blur-[20px]"
          style={{ background: 'radial-gradient(circle at 40% 45%, rgba(108,92,231,.32), rgba(75,159,255,.12) 40%, transparent 68%)' }}
        />
        <div className={`relative ${WRAP} pt-16 md:pt-24 pb-20 md:pb-28`}>
          <nav aria-label="Breadcrumb" className="nd-mono flex flex-wrap items-center gap-2.5 !text-[11px]">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span aria-hidden>/</span>
            <span className="text-white">Services</span>
          </nav>

          <SectionLabel as="h1" num="01" data-testid="services-eyebrow" className="mt-12 md:mt-16">
            Web, App &amp; AI Development Services for US Businesses
          </SectionLabel>
          <WordReveal
            as="p"
            text="We build the products founders are too close to see clearly."
            accent="clearly"
            data-testid="services-headline"
            className="mt-8 max-w-[16ch] font-display font-semibold text-[clamp(44px,7.4vw,128px)] leading-[0.92] tracking-[-0.05em]"
          />

          <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <Reveal delay={250} className="lg:col-span-5 nd-mono flex flex-wrap gap-x-8 gap-y-3 order-2 lg:order-1">
              <span>06 disciplines</span>
              <span>In-house team</span>
              <span>Fixed scope or retainer</span>
            </Reveal>
            <Reveal delay={350} className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2">
              <p className="text-lg md:text-xl text-[#C9C9D3] leading-relaxed max-w-[48ch]">
                Pick the engagement; we’ll bring the people. Website development, web and mobile apps,
                custom software, ecommerce and AI — each with a defined week-zero brief, a measurable
                launch, and a quiet post-launch month.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <MagneticButton href={SITE.whatsapp} external variant="primary">Book discovery call</MagneticButton>
                <MagneticButton to="/work/" variant="ghost">See our work</MagneticButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INDEX LIST */}
      <section ref={indexRef} className="relative border-t border-white/[0.08]">
        <div className={WRAP}>
          {SERVICES.map((s, i) => (
            <article
              key={s.slug}
              id={s.slug}
              data-testid={`service-${s.slug}`}
              className="svc-row group relative isolate scroll-mt-32 border-b border-white/[0.08] py-[clamp(48px,6vw,96px)] grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-8"
            >
              <span aria-hidden className={`absolute inset-y-0 -inset-x-4 md:-inset-x-12 -z-10 origin-bottom scale-y-0 bg-[linear-gradient(90deg,rgba(108,92,231,.10),rgba(75,159,255,.03))] transition-transform [transition-duration:550ms] ${EASE} group-hover:scale-y-100`} />

              {/* number + engagement length */}
              <div className="lg:col-span-2 flex lg:flex-col justify-between lg:justify-start gap-6">
                <div>
                  <div className="nd-mono">{s.num} / 06</div>
                  <div className="svc-rule mt-4 h-px w-24 origin-left" style={{ background: 'var(--nd-grad)' }} />
                </div>
                <div className="lg:mt-10 text-right lg:text-left">
                  <div className="nd-mono !text-[11px]">Engagement</div>
                  <div className="mt-2 font-display text-lg tracking-[-0.02em] text-white/90 tabular-nums">
                    {LENGTHS[i] || '8–12 wks'}
                  </div>
                  <div className="text-[13px] text-ink-muted">fixed scope or retainer</div>
                </div>
              </div>

              {/* title, copy, deliverables */}
              <div className="lg:col-span-6">
                <Reveal>
                  <h2 className={`font-display font-semibold text-[clamp(34px,4.6vw,72px)] leading-[0.96] tracking-[-0.045em] text-balance transition-transform [transition-duration:550ms] ${EASE} lg:group-hover:translate-x-3`}>
                    {s.title}
                  </h2>
                </Reveal>
                <Reveal delay={100}>
                  <p className="mt-6 text-ink-muted text-[17px] leading-relaxed max-w-[56ch]">{s.long}</p>
                </Reveal>
                <Reveal delay={160}>
                  <div className="mt-8 flex flex-wrap gap-2">
                    {s.deliverables.map((d) => (
                      <span key={d} className="font-mono text-[11px] tracking-[.04em] text-[#C9C9D3] px-2.5 py-1.5 border border-white/[0.14] rounded-md">
                        {d}
                      </span>
                    ))}
                  </div>
                </Reveal>
              </div>

              {/* image + links */}
              <div className="lg:col-span-4 flex flex-col gap-8">
                {HERO_IMAGES[s.slug] && (
                  <ClipReveal className="relative aspect-[16/11] rounded-[18px] overflow-hidden bg-ink-surface ring-1 ring-white/[0.08]">
                    <Link to={`/services/${s.slug}/`} tabIndex={-1} aria-hidden data-cursor="View" className="absolute inset-0 block">
                      <span className="svc-img absolute -inset-y-[6%] inset-x-0 block">
                        <img
                          src={HERO_IMAGES[s.slug].src.replace('w=1400', 'w=900')}
                          alt={HERO_IMAGES[s.slug].alt}
                          loading="lazy"
                          decoding="async"
                          referrerPolicy="no-referrer"
                          className={`h-full w-full object-cover [filter:grayscale(.45)_contrast(1.05)] transition-[transform,filter] [transition-duration:1100ms] ${EASE} group-hover:scale-[1.05] group-hover:[filter:grayscale(0)_contrast(1.05)]`}
                        />
                      </span>
                      <span className="absolute inset-0 bg-[linear-gradient(160deg,rgba(108,92,231,.28),rgba(10,10,15,.1)_55%,rgba(10,10,15,.55))]" />
                    </Link>
                  </ClipReveal>
                )}
                <Reveal delay={200} className="flex flex-col gap-4">
                  <Link
                    to={`/services/${s.slug}/`}
                    data-testid={`service-detail-${s.slug}`}
                    className="group/link flex items-center justify-between gap-4 border-t border-white/[0.08] pt-5 text-white"
                  >
                    <span className="font-display text-xl tracking-[-0.02em]">Read the full page</span>
                    <Disc />
                  </Link>
                  <a
                    href={SITE.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-cta-${s.slug}`}
                    className="nd-link-u self-start text-sm text-[#C9C9D3] hover:text-white transition-colors"
                  >
                    Start with this <span aria-hidden>↗</span>
                  </a>
                </Reveal>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ENGAGEMENT MODEL */}
      <section className={SECTION}>
        <div className={WRAP}>
          <SectionHead
            num="02"
            label="How we engage"
            heading="Three ways to work with us."
            accent="work with us."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.08] rounded-[18px] overflow-hidden ring-1 ring-white/[0.08]">
            {ENGAGEMENTS.map((p, i) => (
              <Reveal key={p.title} delay={i * 80} className="bg-ink-bg">
                <div
                  onMouseMove={onMove}
                  data-testid={`engagement-${p.title.toLowerCase()}`}
                  className="group relative h-full min-h-[340px] p-8 md:p-10 flex flex-col overflow-hidden"
                >
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: 'radial-gradient(420px circle at var(--mx,50%) var(--my,50%), rgba(108,92,231,.16), transparent 60%)' }}
                  />
                  <div className="relative flex items-center justify-between">
                    <span className="nd-mono !text-[11px]">{p.length}</span>
                    <span className="nd-mono !text-[11px]">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="relative mt-auto pt-16 font-display font-semibold text-[clamp(40px,4vw,64px)] tracking-[-0.05em] leading-none">{p.title}</div>
                  <div className="relative mt-3 self-start nd-grad-text font-display text-xl font-medium tabular-nums">{p.price}</div>
                  <p className="relative mt-5 text-ink-muted leading-relaxed text-[15px] max-w-md">{p.blurb}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        eyebrow="Pick one, or start with a conversation"
        headline="Tell us what you’re building."
        accent="building."
        sub="We reply within a working day. If you’re a good fit we’ll say so. If not, we’ll introduce you to someone who is."
      />
    </div>
  );
}
